import { patch } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError, isAxiosError } from "axios";
import { z } from "zod";

import { ESignWorkflowSessionStatus } from "./constants";
import { type ESignWorkflowSessionResponse, eSignWorkflowSessionResponseSchema } from "./types";

const updateESignWorkflowSessionAttributesSchema = z.object({
  status: z.enum([
    ESignWorkflowSessionStatus.START,
    ESignWorkflowSessionStatus.SIGNED,
    ESignWorkflowSessionStatus.ERRORED,
  ]),
});

type UpdateESignWorkflowSessionAttributes = z.infer<
  typeof updateESignWorkflowSessionAttributesSchema
>;

const updateESignWorkflowSessionSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.eSignWorkflowSession),
    id: z.string(),
    attributes: updateESignWorkflowSessionAttributesSchema,
  }),
});

export function useUpdateESignWorkflowSession(
  options: UseMutationOptions<
    ESignWorkflowSessionResponse | undefined,
    AxiosError,
    {
      requirementId: string;
      sessionId: string;
      attributesToUpdate: UpdateESignWorkflowSessionAttributes;
    }
  > = {}
): UseMutationResult<
  ESignWorkflowSessionResponse | undefined,
  AxiosError,
  {
    requirementId: string;
    sessionId: string;
    attributesToUpdate: UpdateESignWorkflowSessionAttributes;
  }
> {
  return useMutation({
    mutationFn: async (params: {
      requirementId: string;
      sessionId: string;
      attributesToUpdate: UpdateESignWorkflowSessionAttributes;
    }) => {
      const { requirementId, sessionId, attributesToUpdate } = params;

      try {
        const response = await patch({
          url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/requirements/${requirementId}/esign-workflow-sessions/${sessionId}`,
          queryParams: {
            include: "documents",
          },
          data: {
            data: {
              type: API_TYPES.eSignWorkflowSession,
              id: sessionId,
              attributes: attributesToUpdate,
            },
          },
          requestSchema: updateESignWorkflowSessionSchema,
          responseSchema: eSignWorkflowSessionResponseSchema,
        });

        return response.data;
      } catch (error: unknown) {
        const isExpectedConflict =
          attributesToUpdate.status === ESignWorkflowSessionStatus.SIGNED &&
          isAxiosError(error) &&
          error.response?.status === 409;
        if (isExpectedConflict) {
          return undefined;
        }

        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_ESIGN_WORKFLOW_SESSION_FAILURE,
    },
    ...options,
  });
}
